import { useEffect, useRef } from "react";
import { useSeoMetadata } from "@/hooks/useSeoMetadata";

const BASE_URL = "https://smartly.net";
const DEFAULT_TITLE = "IT Solutions & Cyber Security | Smartly Technologies";
const DEFAULT_DESCRIPTION = "Smartly Technologies provides managed IT support, cyber security, web & mobile development, cloud solutions, and AI integration for modern businesses.";
const DEFAULT_OG_IMAGE = "https://storage.googleapis.com/gpt-engineer-file-uploads/XcQUTjgyh5YSAs95nBtD4k2Lyq13/social-images/social-1770719686553-v1.0-Smartly_OG_Image_4800x2520_BLKBG.png";

interface SEOProps {
  title?: string;
  description?: string;
  canonicalPath?: string;
  ogImage?: string;
  ogType?: string;
  jsonLd?: Record<string, unknown> | Record<string, unknown>[];
  pageSlug?: string;
  noindex?: boolean;
}

function setMetaTag(attr: string, key: string, content: string) {
  let el = document.querySelector(`meta[${attr}="${key}"]`) as HTMLMetaElement | null;
  if (!el) {
    el = document.createElement("meta");
    el.setAttribute(attr, key);
    document.head.appendChild(el);
  }
  el.setAttribute("content", content);
}

function setCanonical(href: string) {
  const links = Array.from(
    document.querySelectorAll('link[rel="canonical"]')
  ) as HTMLLinkElement[];
  // Remove any stray canonicals (e.g. injected by third-party scripts like Cinema8).
  links.slice(1).forEach((l) => l.remove());
  let el = links[0] ?? null;
  if (!el) {
    el = document.createElement("link");
    el.setAttribute("rel", "canonical");
    document.head.appendChild(el);
  }
  el.setAttribute("href", href);
}

const SEO = ({
  title = DEFAULT_TITLE,
  description = DEFAULT_DESCRIPTION,
  canonicalPath = "/",
  ogImage = DEFAULT_OG_IMAGE,
  ogType = "website",
  jsonLd,
  pageSlug,
  noindex = false,
}: SEOProps) => {
  const { data: dbSeo, isLoading: dbLoading, isFetched: dbFetched } = useSeoMetadata(pageSlug);
  const scriptRefs = useRef<HTMLScriptElement[]>([]);

  // When a pageSlug is provided, wait for the DB lookup to settle before
  // applying the title/meta. This prevents a flash of the prop fallback
  // before the saved CMS title is applied.
  const dbReady = pageSlug === undefined ? true : dbFetched && !dbLoading;

  const finalTitle = dbSeo?.seoTitle || title;
  const finalDescription = dbSeo?.metaDescription || description;
  const finalOgTitle = dbSeo?.ogTitle || finalTitle;
  const finalOgDescription = dbSeo?.ogDescription || finalDescription;
  const finalOgImage = dbSeo?.ogImage || ogImage;
  const finalCanonicalUrl = dbSeo?.canonicalUrl || `${BASE_URL}${canonicalPath}`;
  const finalNoindex = Boolean(dbSeo?.noindex || noindex);

  useEffect(() => {
    if (!dbReady) return;

    const prevTitle = document.title;
    document.title = finalTitle;

    setMetaTag("name", "description", finalDescription);
    setMetaTag("name", "robots", finalNoindex ? "noindex, nofollow" : "index, follow");
    setMetaTag("property", "og:title", finalOgTitle);
    setMetaTag("property", "og:description", finalOgDescription);
    setMetaTag("property", "og:url", finalCanonicalUrl);
    setMetaTag("property", "og:image", finalOgImage);
    setMetaTag("property", "og:type", ogType);
    setMetaTag("name", "twitter:title", finalOgTitle);
    setMetaTag("name", "twitter:description", finalOgDescription);
    setMetaTag("name", "twitter:image", finalOgImage);
    setMetaTag("name", "twitter:card", "summary_large_image");
    setCanonical(finalCanonicalUrl);

    // Clean up previous JSON-LD scripts
    scriptRefs.current.forEach((s) => s.remove());
    scriptRefs.current = [];

    // JSON-LD structured data — prefer DB if available
    const finalJsonLd = (dbSeo?.jsonld && dbSeo.jsonld.length > 0) ? dbSeo.jsonld : jsonLd;
    if (finalJsonLd) {
      const items = Array.isArray(finalJsonLd) ? finalJsonLd : [finalJsonLd];
      items.forEach((item) => {
        const scriptEl = document.createElement("script");
        scriptEl.type = "application/ld+json";
        scriptEl.textContent = JSON.stringify(item);
        document.head.appendChild(scriptEl);
        scriptRefs.current.push(scriptEl);
      });
    }

    return () => {
      document.title = prevTitle;
      scriptRefs.current.forEach((s) => s.remove());
      scriptRefs.current = [];
    };
  }, [dbReady, finalTitle, finalDescription, finalCanonicalUrl, finalOgTitle, finalOgDescription, finalOgImage, finalNoindex, ogType, jsonLd, dbSeo]);

  return null;
};

export default SEO;
